/**
 * Grid View Component
 *
 * Main orchestrator for the zone-based grid visualization
 */

import React from 'react'
import { useSyncedStore } from '@syncedstore/react'
import { todoStore } from 'src/store/todo'
import type { TaskType, Worker } from 'src/store/todo/types'
import { useWorkersByZone, useGridLayout } from '../../hooks'
import { checkForDuplicates } from 'src/utils/duplicates'

import TaskFilter from './task-filter'
import ZoneGrid from './zone-grid'

type GridViewProps = {
  /** Currently selected team ID */
  selectedTeamId: string
  /** Array of selected task IDs for filtering */
  selectedTasks: string[]
  /** Callback when a task filter is toggled */
  onToggleTask: (taskId: string) => void
  /** Parcel ID for filtering zones */
  parcelId: string
}

/**
 * GridView component orchestrating the zone grid display
 *
 * @example
 * <GridView
 *   selectedTeamId={teamId}
 *   selectedTasks={selectedTasks}
 *   onToggleTask={handleToggle}
 *   parcelId={parcelId}
 * />
 */
const GridView: React.FC<GridViewProps> = ({
  selectedTeamId,
  selectedTasks,
  onToggleTask,
  parcelId,
}) => {
  const state = useSyncedStore(todoStore)

  // Get filtered data from synced store
  const workers =
    (state.workers?.filter((w: any) => w.teamId === selectedTeamId && w.isActive) as Worker[]) ||
    []

  // Check for duplicates and warn
  React.useEffect(() => {
    checkForDuplicates(workers, 'Workers')
  }, [workers.length])
  const zones =
    state.zones?.filter((z: any) => z.parcelId === parcelId && z.isActive) || []
  const tasks = (state.taskTypes || []) as TaskType[]

  // Build workers map for quick lookup
  const workersMap = React.useMemo(() => {
    const map: Record<string, Worker> = {}
    workers.forEach((w) => {
      map[w.id] = w
    })
    return map
  }, [workers])

  // Build tasks map for quick lookup
  const tasksMap = React.useMemo(() => {
    const map: Record<string, TaskType> = {}
    tasks.forEach((t) => {
      map[t.id] = t
    })
    return map
  }, [tasks])

  // Get workers grouped by zone (reactive to synced store updates)
  const workersByZone = useWorkersByZone(selectedTeamId, selectedTasks)

  // Get grid layout (memoized)
  const gridLayout = useGridLayout(zones)

  return (
    <div className="flex flex-col gap-6 py-6 pb-32">
      {/* Task Filter */}
      <TaskFilter tasks={tasks} selectedTasks={selectedTasks} onToggleTask={onToggleTask} />

      {/* Zone Grid */}
      <ZoneGrid
        gridLayout={gridLayout}
        workersByZone={workersByZone}
        workersMap={workersMap}
        tasks={tasks}
        tasksMap={tasksMap}
        logs={state.workerAssignmentLogs}
        teamId={selectedTeamId}
      />
    </div>
  )
}

export default GridView
